import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DEFAULT_HEADER, IHeader, ERelationType, EEntityType } from '@web/dtos';
import { map, Observable, skip, Subscription } from 'rxjs';
import { AccessibilityBannerComponent } from '../../../components/layout/accessibility-banner/accessibility-banner.component';
import { ButtonComponent } from '../../../components/feedback/button/button.component';
import { ResetChangesDialogComponent } from '../../../components/dialogs/reset-changes-dialog/reset-changes-dialog.component';
import { ButtonGroupComponent } from '../../../components/data-input/form-inputs/button-group/button-group.component';
import { ColorPickerInputComponent } from '../../../components/data-input/form-inputs/color-picker-input/color-picker-input.component';
import { ImageSelectComponent } from '../../../components/data-input/form-inputs/image-select/image-select.component';
import { LanguageSwitcherComponent } from '../../../components/data-input/form-inputs/language-switcher/language-switcher.component';
import { NumberInputComponent } from '../../../components/data-input/form-inputs/number-input/number-input.component';
import { TranslationAccessibilityTextInputComponent } from '../../../components/data-input/form-inputs/translation-accessibility-text-input/translation-accessibility-text-input.component';
import { TranslationTextInputComponent } from '../../../components/data-input/form-inputs/translation-text-input/translation-text-input.component';
import { HeaderComponent } from '../../../components/data-display/header/header.component';
import { LoadingAnimationComponent } from '../../../components/feedback/loading-animation/loading-animation.component';
import { PreviewComponent } from '../../../components/feedback/preview/preview.component';
import { RevealFormErrorsButtonComponent } from '../../../components/feedback/reveal-form-errors-button/reveal-form-errors-button.component';
import { SectionHeaderActionsComponent } from '../../../components/layout/section/section-header/section-header-actions/section-header-actions.component';
import { SectionHeaderComponent } from '../../../components/layout/section/section-header/section-header.component';
import { SectionHeadlineComponent } from '../../../components/layout/section/section-header/section-headline/section-headline.component';
import { SectionComponent } from '../../../components/layout/section/section.component';
import {
  ECMSFontColors,
  ECMSFontSize,
} from '../../../constants/buttonGroups.enums';
import { IHeaderForm } from '../../../entities/header-entity';
import { HeaderFormsFactoryService } from '../../../factory/header/header-forms-factory.service';
import { EnumToButtonOptionArrayPipe } from '../../../pipes/enum-to-button-option-array/enum-to-button-option-array.pipe';
import { HeaderStore } from '../../../services/api/header/header-store.service';
import { ButtonGroupSupportService } from '../../../services/support/button-group-support/button-group-support.service';
import { FormTracking } from '../form-tracking/form-tracking.component';
import { Router } from '@angular/router';
import { ElementFocusService } from '../../../services/support/element-focus-support/element-focus-support.service';
import { DialogService } from '../../../services/support/dialog/dialog.service';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';

@Component({
  selector: 'aw-header-page',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    AccessibilityBannerComponent,
    ButtonComponent,
    ColorPickerInputComponent,
    LanguageSwitcherComponent,
    SectionComponent,
    SectionHeaderActionsComponent,
    SectionHeaderComponent,
    SectionHeadlineComponent,
    TranslationTextInputComponent,
    ImageSelectComponent,
    TranslationAccessibilityTextInputComponent,
    NumberInputComponent,
    ButtonGroupComponent,
    EnumToButtonOptionArrayPipe,
    LoadingAnimationComponent,
    RevealFormErrorsButtonComponent,
    PreviewComponent,
  ],
  templateUrl: './header-page.component.html',
  styles: ``,
})
export class HeaderPageComponent
  extends FormTracking<IHeaderForm>
  implements OnInit, OnDestroy
{
  private readonly elementFocusService: ElementFocusService =
    inject(ElementFocusService);
  readonly buttonGroupSupportService: ButtonGroupSupportService = inject(
    ButtonGroupSupportService
  );
  readonly headerStore: HeaderStore = inject(HeaderStore);
  readonly headerFormsFactory: HeaderFormsFactoryService = inject(
    HeaderFormsFactoryService
  );
  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  readonly dialogService: DialogService = inject(DialogService);
  readonly router: Router = inject(Router);

  public headerForm!: FormGroup<IHeaderForm>;
  protected header: IHeader | undefined;
  public languageForm: FormGroup = new FormGroup({
    languageSelect: new FormControl('DE', [Validators.required]),
  });
  public readonly headlineFontSizeBtn: FormControl<string> = new FormControl();
  public readonly fontColorBtn: FormControl<string> = new FormControl();
  public readonly backgroundColorBtn: FormControl<string> = new FormControl();

  protected readonly fontColors: typeof ECMSFontColors = ECMSFontColors;
  protected readonly fontSizes: typeof ECMSFontSize = ECMSFontSize;
  protected readonly TextRelationType: typeof ERelationType = ERelationType;
  protected readonly EntityType: typeof EEntityType = EEntityType;

  private readonly subscription: Subscription = new Subscription();

  protected readonly HeaderComponent: typeof HeaderComponent = HeaderComponent;
  previewData: WritableSignal<IHeader[]> = signal([]);

  constructor() {
    super();
    effect(() => {
      this.header = this.deepCopyService.createDeepCopy(
        this.headerStore.header()
      );
      this.previewData = signal([this.header]);
      this.headerForm = this.headerFormsFactory.buildForm(this.header);
      this.initFormTracking(this.headerForm);
      this.setInitialButtonStates(this.headerStore.header());
      this.createPreviewListener();
    });
    effect(() => {
      this.elementFocusService.focusElement(
        !this.headerStore.isLoading() && !!this.headerStore.header()
      );
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
  ngOnInit(): void {
    window.onbeforeunload = (event: BeforeUnloadEvent): void => {
      if (this.headerForm.dirty) {
        event.preventDefault();
      }
    };
  }

  save(): boolean {
    if (!this.headerForm.valid) {
      this.headerForm.markAllAsTouched();
      return false;
    }
    this.headerStore.save(this.headerFormsFactory.buildEntity(this.headerForm));
    this.headerForm.markAsPristine();
    return true;
  }

  private createPreviewListener() {
    this.subscription.add(
      this.headerForm.valueChanges.subscribe(value => {
        this.previewData.set([
          this.headerFormsFactory.buildEntity(this.headerForm),
        ]);
      })
    );
  }

  private setInitialButtonStates(header: IHeader): void {
    if (header.headlineFontSize === DEFAULT_HEADER.headlineFontSize) {
      this.headlineFontSizeBtn.setValue(ECMSFontSize.Standard);
      this.headerForm.controls.headlineFontSize.disable();
    } else {
      this.headlineFontSizeBtn.setValue(ECMSFontSize.Benutzerdefiniert);
    }

    this.fontColorBtn.setValue(
      this.buttonGroupSupportService.findValueOnEnum(
        header.fontColor.toString(),
        this.fontColors,
        ECMSFontColors.Benutzerdefiniert
      )
    );
    this.onFontColorChange(this.fontColorBtn.value);

    this.backgroundColorBtn.setValue(
      this.buttonGroupSupportService.findValueOnEnum(
        header.backgroundColor.toString(),
        this.fontColors,
        ECMSFontColors.Benutzerdefiniert
      )
    );
    this.onBackgroundColorChange(this.backgroundColorBtn.value);
  }

  onFontColorChange(value: string): void {
    switch (value) {
      case ECMSFontColors.Benutzerdefiniert:
        this.headerForm.controls.fontColor.enable();
        break;
      default:
        this.headerForm.controls.fontColor.setValue(value);
        this.headerForm.controls.fontColor.disable();
        break;
    }
    this.headerForm.markAsDirty();
  }

  onBackgroundColorChange(value: string): void {
    switch (value) {
      case ECMSFontColors.Benutzerdefiniert:
        this.headerForm.controls.backgroundColor.enable();
        break;
      default:
        this.headerForm.controls.backgroundColor.setValue(value);
        this.headerForm.controls.backgroundColor.disable();
        break;
    }
    this.headerForm.markAsDirty();
  }

  onHeadlinesFontSizeChange(value: string): void {
    switch (value) {
      case ECMSFontSize.Benutzerdefiniert:
        this.headerForm.controls.headlineFontSize.enable();
        break;
      case ECMSFontSize.Standard:
        this.headerForm.controls.headlineFontSize.setValue(
          DEFAULT_HEADER.headlineFontSize
        );
        this.headerForm.controls.headlineFontSize.disable();
        break;
    }
    this.headerForm.markAsDirty();
  }

  deleteImageRight() {
    this.headerForm.controls.rightImage.setValue({
      id: this.headerForm.controls.rightImage.controls.id.value,
      image: null,
      imageAlts: [],
      url: null,
      positionOrder: 0,
    });
    this.headerForm.markAsDirty();
  }

  deleteImageLeft() {
    this.headerForm.controls.leftImage.setValue({
      id: this.headerForm.controls.leftImage.controls.id.value,
      image: null,
      imageAlts: [],
      url: null,
      positionOrder: 0,
    });
    this.headerForm.markAsDirty();
  }

  resetFormChanges() {
    this.headerStore.reload();
  }

  public handleAbortChangesDialog(): Observable<boolean> {
    const dialogRef = this.dialogService.openDialog<{
      icon: string;
      iconBackgroundColor: string;
    }>(ResetChangesDialogComponent, {
      headline: this.cmsTextProvider.shared.revertChanges,
      width: '600px',
      icon: 'warning',
      iconBackgroundColor: '#eed202',
    });
    dialogRef.closed.subscribe(result => {
      if (result != undefined && result.submit) {
        if (result.acceptChanges) {
          this.save();
        } else {
          this.resetFormChanges();
        }
      }
    });
    return dialogRef.closed.pipe(map(result => result?.submit ?? false));
  }
}
